/* eslint-disable max-len */
import {
  BACKOFFICE_ADD_R1_EMAIL_TO_NOTIFICATION_LIST_RESPONSE,
  BACKOFFICE_CHANGE_R1_NOTIFY_ON_BANNER_RESPONSE,
  BACKOFFICE_CHANGE_R1_NOTIFY_ON_COUNTRY_BANNER_RESPONSE,
  BACKOFFICE_CHANGE_R1_NOTIFY_ON_COUNTRY_PROMOTION_RESPONSE,
  BACKOFFICE_CHANGE_R1_NOTIFY_ON_PROMOTION_RESPONSE,
  BACKOFFICE_GET_R1_NETWORK_RESPONSE,
  BACKOFFICE_REMOVE_R1_EMAIL_FROM_NOTIFICATION_LIST_RESPONSE,
  BACKOFFICE_SAVE_RETURNS_FEATURE_SETTINGS_RESPONSE,
  BACKOFFICE_SUBSCRIBE_R1_NETWORK_RESPONSE,
  GET_MY_NETWORK_CLIENT_LIST_RESPONSE,
  UPDATE_MY_NETWORK_CLIENT_RESPONSE,
} from '@1po/1po-bff-fe-spec/generated/common/ResponseType';
import { FilterAndSortField as MyNetworkClientsFilterAndSortField } from '@1po/1po-bff-fe-spec/generated/my_network/request/model/FilterAndSortField';
import { UpdateMyNetworkClientRequest } from '@1po/1po-bff-fe-spec/generated/my_network/request/UpdateMyNetworkClientRequest';
import {
  Client,
  MyNetworkClientsResponse,
} from '@1po/1po-bff-fe-spec/generated/my_network/response/MyNetworkClientsResponse';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RootState } from 'app/AppStore';
import {
  ADD_R1_EMAIL_TO_NOTIFICATION_LIST_REQUEST,
  CHANGE_R1_NOTIFY_ON_BANNER_REQUEST,
  CHANGE_R1_NOTIFY_ON_COUNTRY_BANNER_REQUEST,
  CHANGE_R1_NOTIFY_ON_COUNTRY_PROMOTION_REQUEST,
  CHANGE_R1_NOTIFY_ON_PROMOTION_REQUEST,
  ClientConfig,
  GET_MY_NETWORK_CLIENTS_LIST_REQUEST,
  GET_R1_NETWORK_REQUEST,
  MyNetworkClientsFilterField,
  MyNetworkClientsSort,
  PromotionSettings,
  R1_NETWORK_NAMESPACE,
  R1NetworkState,
  REMOVE_R1_EMAIL_FROM_NOTIFICATION_LIST_REQUEST,
  ReturnsFeatureSettings,
  SAVE_R1_NETWORK_REQUEST,
  SAVE_RETURNS_FEATURE_SETTINGS_REQUEST,
  SUBSCRIBE_R1_NETWORK_REQUEST,
  UPDATE_MY_NETWORK_CLIENTS_CONFIG_REQUEST,
} from 'domains/r1Network/R1Network.types';
import { FOUND, SEARCH_STATUS, SearchData } from 'utils';

const initialState: R1NetworkState = {
  returnFeatureSettings: {
    isRDReturnFeatureEnabled: false,
    isRDReturnFeatureEnabledByCountry: false,
    isMktpReturnFeatureEnabled: false,
    isMktpReturnFeatureEnabledByCountry: false,
  },
  isDealerPickupEnabled: true,
  clientConfig: {
    searchStatus: undefined,
    data: undefined,
  },
  promotionSettings: {
    isPromotionNotificationEnabled: false,
    isCountryPromotionNotificationEnabled: false,
    isCountryBannerNotificationEnabled: false,
    isBannerNotificationEnabled: false,
    notificationsEmails: [],
  },
  networkClients: {
    clients: {
      searchStatus: undefined,
    },
    selectedClient: undefined,
    filters: new Map<MyNetworkClientsFilterAndSortField, string>(),
    sort: { field: MyNetworkClientsFilterField.COMPANY_NAME, direction: 'ASC' },
    pagination: { pageSize: 50, page: 1 },
    isSubmitting: false,
  },
};

// Saga actions
export const getR1NetworkRequestSaga = createAction(GET_R1_NETWORK_REQUEST);
export const getR1NetworkResponseSaga = createAction(BACKOFFICE_GET_R1_NETWORK_RESPONSE);
export const subscribeR1NetworkRequestSaga = createAction(SUBSCRIBE_R1_NETWORK_REQUEST);
export const subscribeR1NetworkResponseSaga = createAction(BACKOFFICE_SUBSCRIBE_R1_NETWORK_RESPONSE);
export const saveReturnsFeatureSettingsRequestSaga = createAction(SAVE_RETURNS_FEATURE_SETTINGS_REQUEST);
export const saveR1NetworkRequestSaga = createAction<{
  isDealerPickupEnabled: boolean;
}>(SAVE_R1_NETWORK_REQUEST);
export const saveReturnsFeatureSettingsResponseSaga = createAction(BACKOFFICE_SAVE_RETURNS_FEATURE_SETTINGS_RESPONSE);

export const changeR1NotifyOnPromotionRequestSaga = createAction<{
  isPromotionNotificationEnabled: boolean;
}>(CHANGE_R1_NOTIFY_ON_PROMOTION_REQUEST);
export const changeR1NotifyOnPromotionResponseSaga = createAction(BACKOFFICE_CHANGE_R1_NOTIFY_ON_PROMOTION_RESPONSE);

export const changeR1NotifyOnCountryPromotionRequestSaga = createAction<{
  isCountryPromotionNotificationEnabled: boolean;
}>(CHANGE_R1_NOTIFY_ON_COUNTRY_PROMOTION_REQUEST);
export const changeR1NotifyOnCountryPromotionResponseSaga = createAction(
  BACKOFFICE_CHANGE_R1_NOTIFY_ON_COUNTRY_PROMOTION_RESPONSE,
);

export const changeR1NotifyOnBannerRequestSaga = createAction<{
  isBannerNotificationEnabled: boolean;
}>(CHANGE_R1_NOTIFY_ON_BANNER_REQUEST);
export const changeR1NotifyOnBannerResponseSaga = createAction(BACKOFFICE_CHANGE_R1_NOTIFY_ON_BANNER_RESPONSE);

export const changeR1NotifyOnCountryBannerRequestSaga = createAction<{
  isCountryBannerNotificationEnabled: boolean;
}>(CHANGE_R1_NOTIFY_ON_COUNTRY_BANNER_REQUEST);
export const changeR1NotifyOnCountryBannerResponseSaga = createAction(
  BACKOFFICE_CHANGE_R1_NOTIFY_ON_COUNTRY_BANNER_RESPONSE,
);

export const addR1EmailToNotificationListRequestSaga = createAction<{ email: string }>(
  ADD_R1_EMAIL_TO_NOTIFICATION_LIST_REQUEST,
);
export const addR1EmailToNotificationListResponseSaga = createAction(
  BACKOFFICE_ADD_R1_EMAIL_TO_NOTIFICATION_LIST_RESPONSE,
);

export const removeR1EmailFromNotificationListRequestSaga = createAction<{ email: string }>(
  REMOVE_R1_EMAIL_FROM_NOTIFICATION_LIST_REQUEST,
);
export const removeR1EmailFromNotificationListResponseSaga = createAction(
  BACKOFFICE_REMOVE_R1_EMAIL_FROM_NOTIFICATION_LIST_RESPONSE,
);

export const fetchMyNetworkClientsRequestSaga = createAction<{ page?: number; pageSize?: number }>(
  GET_MY_NETWORK_CLIENTS_LIST_REQUEST,
);

export const updateMyNetworkClientsRequestSaga = createAction<UpdateMyNetworkClientRequest>(
  UPDATE_MY_NETWORK_CLIENTS_CONFIG_REQUEST,
);

export const fetchMyNetworkClientsResponseSaga = createAction(GET_MY_NETWORK_CLIENT_LIST_RESPONSE);

export const updateMyNetworkClientsResponseSaga = createAction(UPDATE_MY_NETWORK_CLIENT_RESPONSE);

// Slice
const slice = createSlice({
  name: R1_NETWORK_NAMESPACE,
  initialState,
  reducers: {
    setInitialState: () => initialState,
    setReturnsFeatureSettings: (state, { payload }: PayloadAction<ReturnsFeatureSettings>) => {
      state.returnFeatureSettings = {
        email: payload.email,
        isRDReturnFeatureEnabled: !!payload.isRDReturnFeatureEnabled,
        isRDReturnFeatureEnabledByCountry: !!payload.isRDReturnFeatureEnabledByCountry,
        isMktpReturnFeatureEnabled: !!payload.isMktpReturnFeatureEnabled,
        isMktpReturnFeatureEnabledByCountry: !!payload.isMktpReturnFeatureEnabledByCountry,
      };
    },
    setR1Network: (state, { payload }: PayloadAction<boolean>) => {
      state.isDealerPickupEnabled = payload;
    },
    setClientConfig: (state, { payload }: PayloadAction<SearchData<ClientConfig>>) => {
      state.clientConfig = payload;
    },
    setPromotionSettings: (state, { payload }: PayloadAction<PromotionSettings>) => {
      state.promotionSettings = {
        isPromotionNotificationEnabled: payload.isPromotionNotificationEnabled ?? false,
        isCountryPromotionNotificationEnabled: payload.isCountryPromotionNotificationEnabled ?? false,
        isCountryBannerNotificationEnabled: payload.isCountryBannerNotificationEnabled ?? false,
        isBannerNotificationEnabled: payload.isBannerNotificationEnabled ?? false,
        notificationsEmails: payload.notificationsEmails,
      };
    },
    setMyNetworkClients: (state, { payload }: PayloadAction<MyNetworkClientsResponse>) => {
      state.networkClients.clients = {
        searchStatus: FOUND,
        data: payload,
      };
    },
    setMyNetworkClientsPagination: (state, { payload }: PayloadAction<{ pageSize: number; page: number }>) => {
      state.networkClients.pagination = payload;
    },
    resetMyNetworkClients: (state, { payload }: PayloadAction<SEARCH_STATUS>) => {
      state.networkClients.clients = { ...initialState.networkClients.clients, searchStatus: payload };
    },
    setMyNetworkClientsStatus: (state, { payload }: PayloadAction<SEARCH_STATUS>) => {
      state.networkClients.clients = {
        ...state.networkClients.clients,
        searchStatus: payload,
      };
    },
    setSelectedClient: (state, { payload }: PayloadAction<Client>) => {
      state.networkClients.selectedClient = payload;
    },
    setMyNetworkClientsSubmitting: (state, { payload }: PayloadAction<boolean>) => {
      state.networkClients.isSubmitting = payload;
    },
    setMyNetworkClientsFilter: (
      state,
      { payload }: PayloadAction<{ field: MyNetworkClientsFilterAndSortField; value?: string }>,
    ) => {
      const { field, value } = payload;
      const filters = state.networkClients.filters;
      if (value && value.length > 0) {
        filters.set(field, value);
      } else {
        filters.delete(field);
      }
    },
    resetMyNetworkClientsFilterAndSort: (state) => {
      if (state.networkClients.filters.size > 0) {
        state.networkClients.filters = initialState.networkClients.filters;
      }
      state.networkClients.sort = initialState.networkClients.sort;
    },
    setMyNetworkClientsSort: (state, { payload }: PayloadAction<MyNetworkClientsSort>) => {
      state.networkClients.sort = payload;
    },
  },
});

export const {
  setInitialState,
  setReturnsFeatureSettings,
  setR1Network,
  setClientConfig,
  setPromotionSettings,
  setMyNetworkClients,
  setMyNetworkClientsPagination,
  resetMyNetworkClients,
  setMyNetworkClientsStatus,
  setSelectedClient,
  setMyNetworkClientsSubmitting,
  setMyNetworkClientsFilter,
  resetMyNetworkClientsFilterAndSort,
  setMyNetworkClientsSort,
} = slice.actions;

// Getters/Selectors
export const getR1Network = createSelector(
  (state: RootState) => state.r1Network,
  (r1Network: R1NetworkState) => r1Network,
);

export const getR1ClientConfig = createSelector(
  (state: RootState) => state.r1Network.clientConfig,
  (clientConfig: SearchData<ClientConfig>) => clientConfig,
);

export const getMyNetworkClients = createSelector(
  (state: RootState) => state.r1Network.networkClients.clients,
  (clients) => clients,
);

export const getMyNetworkClientsPagination = createSelector(
  (state: RootState) => state.r1Network.networkClients.pagination,
  (pagination) => pagination,
);

export const getSelectedClient = createSelector(
  (state: RootState) => state.r1Network.networkClients.selectedClient,
  (selectedClient) => selectedClient,
);

export const getMyNetworkClientsIsSubmitting = createSelector(
  (state: RootState) => state.r1Network.networkClients.isSubmitting,
  (isSubmitting) => isSubmitting,
);

export const getMyNetworkClientsFilters = createSelector(
  (state: RootState) => state.r1Network.networkClients.filters,
  (filters) => filters,
);

export const getMyNetworkClientsSort = createSelector(
  (state: RootState) => state.r1Network.networkClients.sort,
  (sort) => sort,
);

export default slice.reducer;
